import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, from } from 'rxjs';
import { AuthService } from '../services/http/auth.service';
import { StorageService } from '../services/storage.service';

/**
 * Interceptor pour renouveler automatiquement le token quand il expire
 *
 * Quand une requête échoue avec 401 (token expiré) :
 * 1. Récupère le refresh token
 * 2. Appelle /auth/refresh pour obtenir un nouveau access token
 * 3. Sauvegarde le nouveau token
 * 4. Rejoue la requête originale avec le nouveau token
 * 5. Si le refresh échoue, déconnecte et redirige vers login
 */
export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const storageService = inject(StorageService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Ignorer si ce n'est pas une erreur 401
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Ignorer les erreurs 401 sur les endpoints /auth/login et /auth/refresh
      // (sinon on crée une boucle infinie)
      const url = req.url.toLowerCase();
      if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
        return throwError(() => error);
      }

      // Récupérer le refresh token
      const refreshToken = storageService.getRefreshToken();

      // Si pas de refresh token, ne pas rediriger automatiquement
      // Laisser l'erreur 401 se propager pour que le composant décide quoi faire
      if (!refreshToken) {
        console.log('[TokenRefresh] Pas de refresh token, propagation de l\'erreur 401');
        return throwError(() => error);
      }

      console.log('[TokenRefresh] Token expiré, tentative de renouvellement...');

      // Tenter de renouveler le token
      return from(authService.refreshToken(refreshToken)).pipe(
        switchMap((response) => {
          console.log('[TokenRefresh] Token renouvelé avec succès');

          // Sauvegarder le nouveau token (garder le même storage et refresh token)
          const rememberMe = storageService.isRememberMeActive();
          storageService.setToken(response.access_token, rememberMe, refreshToken);

          // Cloner la requête originale avec le nouveau token
          const clonedRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${response.access_token}`
            }
          });

          // Rejouer la requête originale
          return next(clonedRequest);
        }),
        catchError((refreshError) => {
          // Le refresh a échoué (refresh token expiré ou invalide)
          console.error('[TokenRefresh] Échec du renouvellement, déconnexion', refreshError);
          storageService.clearAuth();
          router.navigate(['/login']);
          return throwError(() => refreshError);
        })
      );
    })
  );
};
